import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';

// Google Analytics 4 (GA4) tracking
const GA_TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID || 'G-TFJZXD8Q4Q';

// Initialize Consent Mode before GA
export const initConsentMode = () => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    // Set default consent state (denied by default for privacy)
    gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }
};

// Initialize Google Analytics
export const initGA = () => {
  if (typeof window !== 'undefined' && GA_TRACKING_ID) {
    // Load Google Analytics script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
    document.head.appendChild(script1);

    // Initialize gtag if not already initialized
    if (!window.gtag) {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;
    }

    window.gtag('js', new Date());
    window.gtag('config', GA_TRACKING_ID, {
      page_title: document.title,
      page_location: window.location.href,
    });

    // Update consent based on saved preferences
    const savedConsent = Cookies.get('bailebelle_cookie_consent');
    if (savedConsent) {
      try {
        const consentData = JSON.parse(savedConsent);
        if (consentData.analytics || consentData.marketing) {
          window.gtag('consent', 'update', {
            analytics_storage: consentData.analytics ? 'granted' : 'denied',
            ad_storage: consentData.marketing ? 'granted' : 'denied',
            ad_user_data: consentData.marketing ? 'granted' : 'denied',
            ad_personalization: consentData.marketing ? 'granted' : 'denied',
          });
        }
      } catch (error) {
        console.error('Error parsing consent data:', error);
      }
    }
  }
};

// Check if consent is granted
const hasConsent = (type) => {
  if (typeof window === 'undefined') return false;

  const savedConsent = Cookies.get('bailebelle_cookie_consent');
  if (!savedConsent) return false;

  try {
    const consentData = JSON.parse(savedConsent);
    if (type === 'analytics') return consentData.analytics === true;
    if (type === 'marketing') return consentData.marketing === true;
    return false;
  } catch (error) {
    return false;
  }
};

// Track page views
export const trackPageView = (url) => {
  if (typeof window !== 'undefined' && window.gtag && hasConsent('analytics')) {
    window.gtag('config', GA_TRACKING_ID, {
      page_path: url,
    });
  }
};

// Track custom events
export const trackEvent = (action, category, label, value) => {
  if (typeof window !== 'undefined' && window.gtag && hasConsent('analytics')) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track e-commerce events
export const trackPurchase = (transactionId, value, currency = 'USD', items = []) => {
  if (typeof window !== 'undefined' && window.gtag && hasConsent('analytics')) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      items: items,
    });
  }
};

export const trackAddToCart = (itemId, itemName, category, price, quantity = 1) => {
  if (typeof window !== 'undefined' && window.gtag && hasConsent('analytics')) {
    window.gtag('event', 'add_to_cart', {
      currency: 'USD',
      value: price * quantity,
      items: [
        {
          item_id: itemId,
          item_name: itemName,
          category: category,
          price: price,
          quantity: quantity,
        },
      ],
    });
  }
};

export const trackViewItem = (itemId, itemName, category, price) => {
  if (typeof window !== 'undefined' && window.gtag && hasConsent('analytics')) {
    window.gtag('event', 'view_item', {
      currency: 'USD',
      value: price,
      items: [
        {
          item_id: itemId,
          item_name: itemName,
          category: category,
          price: price,
        },
      ],
    });
  }
};

// Track begin checkout event
export const trackBeginCheckout = (value, currency = 'USD', items = []) => {
  if (typeof window !== 'undefined' && window.gtag && hasConsent('analytics')) {
    window.gtag('event', 'begin_checkout', {
      currency: currency,
      value: value,
      items: items,
    });
  }
};

// Component to track page views on route changes
const GoogleAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Initialize consent mode first
    initConsentMode();

    // Initialize GA on component mount
    initGA();

    // Log warning if GA_TRACKING_ID is not set
    if (!GA_TRACKING_ID || GA_TRACKING_ID === 'G-TFJZXD8Q4Q') {
      console.warn('Google Analytics Tracking ID is not configured. Please set REACT_APP_GA_TRACKING_ID in your .env file.');
    }
  }, []);

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
};

export default GoogleAnalytics;
